// Products › Scan (2026-10-31, docs/PRODUCT-SCAN.md): the shapes backend/products_scan.py answers with, shared by the
// Scan page and the Move page.
import { authFetch, API } from '../api';

/** one size of a product in one shop — the WMS's own columns the phone shows and edits */
export interface ScanRow {
  wms_id: number; webshop_id: string; platform: 'wgr' | 'mcf' | 'shopify' | string; external_id: string;
  external_product_id: string | null; article_number: string | null; master_sku: string | null; picklist_number: string | null;
  label_size: string | null; name: string | null; name_local: string | null; variant_title: string | null; size: string | null;
  barcode: string | null; aliases: string[]; stock_location: string | null; stock_location_norm: string; restock_location: string | null;
  stock_qty: number; always_in_stock: boolean | null; min_stock: number | null; weight_g: number | null; is_bulky: boolean | null;
  commodity_code: string | null; commodity_code_full: string | null; country_of_origin: string | null; owned_fields: string[] | null;
  is_active: boolean; last_synced_at: string | null; push_status: Record<string, PushInfo>; is_hit?: boolean;
  [k: string]: any;
}
export interface PushInfo { id: number; platform_field: string; status: string; error: string | null; attempts: number; value: string | null; at: string | null; next_at?: string | null; webshop_id?: string }
export interface ColumnSync { platform: string; field: string; resource: string; on: boolean; store: string }
export interface Column { key: string; label: string; type: string; default: boolean; editable: boolean; group: string; syncs?: Record<string, ColumnSync>; wms_only?: string }
export interface Group { webshop_id: string; platform: string; style_key: string; name: string | null; sizes: ScanRow[]; truncated: boolean }
export interface Shop { name: string; platform: string; pool: boolean }
export interface Rights { stock_location: boolean; products_edit: boolean; can_edit: boolean; device: boolean }

export interface Lookup {
  status: 'ok' | 'unknown' | 'ambiguous' | 'choose_shop';
  code: string; matched_by: '' | 'barcode' | 'alias' | 'article' | 'picklist' | 'wgr_fetch';
  shops: Record<string, Shop>; rights: Rights; writes_mode: 'on' | 'off';
  // ok
  hits?: number[]; this_size?: number[]; pool_shop?: string; style_key?: string; name?: string | null;
  groups?: Group[]; pairs?: number[][]; columns?: Column[];
  // ambiguous (two products of one shop on one code)
  candidates?: (ScanRow & { style_key: string })[];
  // choose_shop (a Motley and a Nora garment on one code)
  options?: { webshop_id: string; name: string; platform: string; pool: boolean; wms_ids: number[] }[];
}

/** what is physically on the shelf for one row (wgr_inventory._expected's shape) */
export interface Expected {
  live: number; unpicked: number; in_flight: number; raw: number; expected: number; infinite: boolean;
  source: 'live' | 'sync'; mirror_of?: number; note?: string | null;
}

export async function lookup(code: string, opts: { shop?: string; pick?: number; wms_id?: number; union?: boolean } = {}): Promise<Lookup> {
  const q = new URLSearchParams();
  if (opts.wms_id) q.set('wms_id', String(opts.wms_id)); else q.set('code', code);
  if (opts.shop) q.set('shop', opts.shop);
  if (opts.pick) q.set('pick', String(opts.pick));
  if (opts.union) q.set('union', '1');           // the table: no questions back, every match's style in every shop
  const r = await authFetch(`${API}/products/scan?${q}`);
  if (!r.ok) throw new Error(await detailOf(r));
  return r.json();
}

export async function expectedFor(ids: number[]): Promise<Record<string, Expected>> {
  if (!ids.length) return {};
  const r = await authFetch(`${API}/products/scan/expected?ids=${ids.join(',')}`);
  if (!r.ok) throw new Error(await detailOf(r));
  return (await r.json()).expected || {};
}

/** the same edit the Products table makes: one field of one row → {product, changed, queued} */
export async function patchRow(wmsId: number, field: string, value: string): Promise<Response> {
  return authFetch(`${API}/products/v2/${wmsId}`, { method: 'PATCH', body: JSON.stringify({ [field]: value }) });
}

export function post(path: string, body: any = {}): Promise<Response> {
  return authFetch(`${API}/products/scan${path}`, { method: 'POST', body: JSON.stringify(body) });
}

export async function detailOf(r: Response, fallback = 'Something went wrong'): Promise<string> {
  const d = await r.json().catch(() => ({} as any));
  if (typeof d.detail === 'string') return d.detail;
  if (d.detail?.message) return d.detail.message;
  return fallback;
}

/** the editable fields in the order assortment work needs them (the catalogue decides WHAT is editable; this only
 *  orders it — a field missing here is still offered under "More fields") */
export const FIELD_ORDER = ['stock_location', 'restock_location', 'picklist_number', 'barcode', 'min_stock', 'is_bulky', 'weight_g',
  'commodity_code_full', 'country_of_origin'];
/** fields that tell one size from another, or are a row's own words — never "applied to all sizes" */
export const PER_SIZE_ONLY = new Set(['article_number', 'barcode', 'picklist_number', 'size', 'master_sku', 'name', 'variant_title']);
/** which floor right an edit of a field needs */
export const rightFor = (field: string): 'stock_location' | 'products_edit' =>
  field === 'stock_location' || field === 'restock_location' ? 'stock_location' : 'products_edit';

// ── the move (P2, migration 127) ──────────────────────────────────────────────
export interface MoveLine {
  wms_id: number; webshop_id: string; platform: string; pool: boolean; article_number: string | null; size: string | null; barcode: string | null;
  from_display: string | null; expected?: number; live?: number; unpicked?: number; in_flight?: number; infinite?: boolean; source?: string;
  counted?: number; delta?: number | null; delta_preview?: number | null; write_id?: number; write_status?: string; write_reason?: string | null;
}
export interface Move {
  id: number; code: string | null; pool_shop: string; style_key: string; name: string | null; pool_ids: number[]; shelf_ids: number[];
  from_display: string | null; to_code: string | null; to_display: string | null;
  status: 'scanning' | 'target' | 'saved' | 'cancelled'; lines: MoveLine[]; scans: { code: string; wms_id: number; how: string; by: string; at: string }[];
  missing: number; surplus: number; count_differs: boolean; writes_mode: string | null; writes_mode_now: 'on' | 'off';
  started_by: string; started_at: string; finished_at: string | null; note: string | null;
}
export interface Other { key: string; webshop_id: string; name: string | null; master_sku: string | null; sizes: { wms_id: number; article_number: string | null; size: string | null; stock: number }[] }
export interface MoveReply { ok?: boolean; move: Move; shops?: Record<string, Shop>; others?: Other[]; rights?: Rights; status?: string; wms_id?: number; size?: string; counted?: number; expected?: number }

export async function moveGet(mid: number | string): Promise<MoveReply> {
  const r = await authFetch(`${API}/products/scan/move/${mid}`);
  if (!r.ok) throw new Error(await detailOf(r));
  return r.json();
}
export async function myMoves(): Promise<{ id: number; name: string | null; style_key: string; from_display: string | null; status: string; started_at: string }[]> {
  const r = await authFetch(`${API}/products/scan/moves`);
  if (!r.ok) return [];
  return (await r.json()).moves || [];
}
